import styled from '@emotion/styled';
import { refractionStimulusBackgrounds, calculateRefractionResult } from '../../../helpers/refraction';
import { useEffect, useState } from 'react';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useTestContext } from '../../../context/testContext';
import { useCalibration } from '../../../context/calibrationContext';
import { ReactComponent as Refractotype1 } from '../../../assets/refraction/refractotype1.svg';
import { ReactComponent as Refractotype2 } from '../../../assets/refraction/refractotype2.svg';
import { ReactComponent as Refractotype3 } from '../../../assets/refraction/refractotype3.svg';
import { ReactComponent as Refractotype4 } from '../../../assets/refraction/refractotype4.svg';
import { ReactComponent as Refractotype5 } from '../../../assets/refraction/refractotype5.svg';
import { ReactComponent as Refractotype6 } from '../../../assets/refraction/refractotype6.svg';
import { ReactComponent as Refractotype7 } from '../../../assets/refraction/refractotype7.svg';
import { ReactComponent as Refractotype8 } from '../../../assets/refraction/refractotype8.svg';
import RefractionTable from '../../../components/Tables/RefractionTable';
import { RadioGroup, SelectField, SessionActions, colors, breakpoints } from '../../../ui';
import { StepBody, Divider, Label, useConfirmLeave } from '../testScreenKit';

const REFRACTOTYPE_OPTIONS = [
  { value: '1', label: 'Refractotype 1' },
  // { value: '2', label: 'Refractotype 2' }, ... '8' (disabled, as before)
];
const POSITION_OPTIONS = [
  { value: '1', label: 'Position 1' },
  { value: '2', label: 'Position 2' },
  { value: '3', label: 'Position 3' },
];
const STIMULUS_SIZE_OPTIONS = [
  // { label: '10mm', value: '10mm' }, { label: '20mm', value: '20mm' }, logMAR sizes (disabled, as before)
  { label: '50mm', value: '50mm' },
];

const Preview = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 1.5;
  border-radius: 4px;
  overflow: hidden;
  justify-self: center;
  box-shadow: 0 0 0 1px ${colors.slate[300]};
  @media (max-width: ${breakpoints.phone - 1}px) {
    max-width: 180px;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 24px;
  align-items: center;
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  .full {
    grid-column: 1 / -1;
  }
  @media (orientation: portrait) and (min-width: ${breakpoints.phone}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const RefractionTest = ({ eyeTested, updateStep }) => {
  const { testData, updateTestData } = useTestContext();
  const [visibleStimulus, setVisibleStimulus] = useState(true);
  const [stimulusBackground, setStimulusBackground] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState('1');
  const [stimulusSize, setStimulusSize] = useState({ label: '50mm', value: '50mm' });
  const [selectedRefractotype, setSelectedRefractotype] = useState({ value: '1', label: 'Refractotype 1' });

  const [canProgress, setCanProgress] = useState(false);
  const confirmLeave = useConfirmLeave();
  const postMessage = usePostMessage();
  const messageFromPatient = useReceiveMessage();
  const {
    calibrationData: { pixelDensity },
  } = useCalibration();
  const [positionData, setPositionData] = useState({
    1: {
      distance: null,
      tracking: false,
    },
    2: {
      distance: null,
      tracking: false,
    },
    3: {
      distance: null,
      tracking: false,
    },
  });

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const getStimulusBackgroundObject = (selectedStimulusBackground) => {
    return refractionStimulusBackgrounds.find((element) => element.value === selectedStimulusBackground);
  };

  const sendNewTarget = () => {
    postMessage({
      type: 'updateTarget',
      content: {
        stimulusBackground: getStimulusBackgroundObject(stimulusBackground),
        stimulusPosition: selectedPosition,
        visibleStimulus: visibleStimulus,
        stimulusSize: stimulusSize.value,
        pixelDensity: pixelDensity,
        stimulusRefractotype: selectedRefractotype.value,
        activeEye: eyeTested,
      },
    });
  };

  useEffect(() => {
    if (positionData[1].distance !== null && positionData[2].distance !== null && positionData[3].distance !== null) {
      setCanProgress(true);
      const results = calculateRefractionResult(positionData[1].distance, positionData[2].distance, positionData[3].distance);
      updateTestData({ [eyeTested]: { ...testData[eyeTested], ...{ score: results } } });
    } else {
      setCanProgress(false);
    }
  }, [positionData, eyeTested, updateTestData]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientDistance') {
      for (let i = 1; i <= 3; i++) {
        if (positionData[i].tracking === true) {
          setPositionData({
            ...positionData,
            [i]: {
              ...positionData[i],
              distance: messageFromPatient.content,
            },
          });
        }
      }
    }
  }, [messageFromPatient]);

  useEffect(() => {
    if (stimulusBackground === null && stimulusSize === null && testData[eyeTested]) {
      postMessage({ type: 'initialiseDetection', content: testData[eyeTested]?.selectedTool?.values });
      setStimulusBackground('white');
    } else {
      sendNewTarget();
    }
  }, [stimulusBackground, selectedPosition, visibleStimulus, eyeTested, postMessage, testData, stimulusSize, selectedRefractotype]);

  const handleBackButton = () => {
    const backStep = eyeTested === 'leftEye' && testData.testRightEye ? 'changeEyePrompt' : 'eyeSetup';
    confirmLeave().then(() => {
      updateTestData({ [eyeTested]: null, testInProgress: false });
      return updateStep(backStep);
    });
  };

  const handleNextButton = () => {
    const nextStep = eyeTested === 'rightEye' && testData.testLeftEye ? 'changeEyePrompt' : 'results';
    return updateStep(nextStep);
  };

  const getRefractotype = (props) => {
    switch (selectedRefractotype.value) {
      case '2':
        return <Refractotype2 {...props} />;
      case '3':
        return <Refractotype3 {...props} />;
      case '4':
        return <Refractotype4 {...props} />;
      case '5':
        return <Refractotype5 {...props} />;
      case '6':
        return <Refractotype6 {...props} />;
      case '7':
        return <Refractotype7 {...props} />;
      case '8':
        return <Refractotype8 {...props} />;
      default:
        return <Refractotype1 {...props} />;
    }
  };

  return (
    <StepBody gap={18} data-testid='refraction-scoring'>
      <Layout>
        <Preview style={{ backgroundColor: [5, 6, 7].includes(Number(selectedRefractotype.value)) ? 'white' : 'black' }} aria-label='Stimulus preview'>
          <div style={{ height: '75%', width: '50%', opacity: visibleStimulus ? 1 : 0.25 }}>
            {getRefractotype({
              style: {
                transform: `rotate(${selectedPosition === '2' ? 60 : selectedPosition === '3' ? -60 : 0}deg)`,
              },
            })}
          </div>
        </Preview>
        <Controls>
          {REFRACTOTYPE_OPTIONS.length > 1 && (
            <SelectField className='full' label='Select Refractotype' placeholder={null} options={REFRACTOTYPE_OPTIONS} value={selectedRefractotype.value} onChange={(v) => setSelectedRefractotype(REFRACTOTYPE_OPTIONS.find((o) => o.value === v))} />
          )}
          <SelectField label='Select Position' placeholder={null} options={POSITION_OPTIONS} value={selectedPosition} onChange={(v) => setSelectedPosition(v)} />
          <SelectField label='Select Stimulus' placeholder={null} options={STIMULUS_SIZE_OPTIONS} value={stimulusSize.value} onChange={(v) => setStimulusSize(STIMULUS_SIZE_OPTIONS.find((o) => o.value === v))} />
          <div className='full' style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Label size={12} weight={700} style={{ color: colors.textMuted }}>
              Stimulus Preview
            </Label>
            <RadioGroup
              ariaLabel='Stimulus preview'
              options={[
                { value: true, label: 'Show' },
                { value: false, label: 'Hide' },
              ]}
              value={visibleStimulus}
              onChange={setVisibleStimulus}
            />
          </div>
        </Controls>
      </Layout>
      <Divider />
      <RefractionTable positionData={positionData} setPositionData={setPositionData} selectedPosition={selectedPosition} />
      <SessionActions onBack={handleBackButton} primary={{ label: 'Next', onClick: handleNextButton, disabled: !canProgress }} />
    </StepBody>
  );
};

export default RefractionTest;
